import React, { useEffect, useState, useMemo } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import io from "socket.io-client";

import Header from "@/components/molecules/Header";
import Button from "@/components/atoms/Buttons/Button";
import i18n from "@/translate/i18n";
import { convertMsToTime, formatCurrency } from "@/utils";
import { useTransactionData } from "@/context/transactionContext";
import { useMerchantTheme } from "@/hooks/useMerchantTheme";

import { Trans } from "react-i18next";

import { LayoutWrapper1xbet } from "@/components/molecules/Wrappers/LayoutWrapper1xbet";
import { FakeInput1xbet } from "@/components/molecules/Wrappers/FakeInput1xbet";
import { ReactComponent as WarningIcon } from "@/assets/images/warning.svg";
import { ReactComponent as SofIcon } from "@/assets/images/sof.svg";
import { ReactComponent as DocumentIcon } from "@/assets/images/document.svg";
import { ReactComponent as MercadoLogo } from "@/assets/images/mercadoLogo.svg";

import { QrCode } from "./styles";

const ONE_SECOND = 1000;
const TEN_MINUTES = 600000;
const QUANTITY_INSTRUCTIONS = 3;

const isAndroid = () => /Android/i.test(navigator.userAgent);
const isIOS = () => /iPhone|iPad|iPod/i.test(navigator.userAgent);

const isValidUrl = (urlString) => {
  if (!urlString) return false;
  try {
    return Boolean(new URL(urlString));
  } catch (e) {
    return false;
  }
};

function TransactionDetails1xbet() {
  const history = useHistory();
  const { id, token } = useParams();
  const { showAlreadyPaid } = useMerchantTheme();
  const { transactionData, setTransactionData } = useTransactionData();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const dataSource = searchParams.get("source");
  const kycRequired = location.state?.kycRequired;

  const isQrPresent = useMemo(
    () => Boolean(transactionData?.bank_payment_data?.qr_code),
    [transactionData],
  );

  const initialTimerState = transactionData?.seconds_to_expire
    ? transactionData.seconds_to_expire * ONE_SECOND
    : TEN_MINUTES;

  const [counter, setCounter] = useState(initialTimerState);
  const validationNeeded = false;

  useEffect(() => {
    if (!transactionData?.status) return;
    if (
      transactionData?.status === "cancelled" ||
      transactionData?.status === "expired"
    ) {
      history.push({
        pathname: `/transaction-status/${id}/${token}`,
        search: location.search,
        state: { data: transactionData },
      });
      return;
    }
    if (transactionData?.status !== "pending") {
      history.push({
        pathname: `/deposit/status/${id}/${token}`,
        search: location.search,
        state: { data: transactionData },
      });
    }
  }, [
    transactionData?.status,
    history,
    id,
    token,
    location.search,
    transactionData,
  ]);

  useEffect(() => {
    if (transactionData && !transactionData.id) {
      history.push(`/${id}/${token}${history.location.search}`);
    }
  }, [transactionData, id, token, history, location.search]);

  useEffect(() => {
    const socket = io(process.env.REACT_APP_SERVER_URL, {
      path: "/websocket/socket.io",
    });
    socket.on("confirmTransaction", (socketData) => {
      if (socketData.id === Number(id)) {
        socket.disconnect();
        const mergedData = { ...transactionData, ...socketData };
        if (socketData?.kyc_required && isValidUrl(socketData.redirect_url)) {
          window.location.replace(socketData.redirect_url);
        } else if (socketData?.status !== "pending") {
          history.push({
            pathname: `/deposit/status/${id}/${token}`,
            search: history.location.search,
            state: { data: mergedData },
          });
        }
      }
    });
    return () => socket.disconnect();
  }, [id, history, token, transactionData]);

  useEffect(() => {
    if (counter <= 0) {
      const expiredData = {
        ...transactionData,
        status: "expired",
        message: "transaction_expired",
      };
      setTransactionData(expiredData);
      history.push({
        pathname: `/deposit/status/${id}/${token}`,
        search: location.search,
        state: { data: expiredData },
      });
      return undefined;
    }
    const timer = setInterval(
      () => setCounter((prev) => prev - ONE_SECOND),
      ONE_SECOND,
    );
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [counter, history, id, token, location.search, setTransactionData]);

  const isTimerRunning = counter > 0;

  return (
    <div className="flex justify-center items-center bg-white sm:bg-gray-900 font-arboria-book min-h-screen overflow-x-hidden">
      <div className="flex flex-col justify-between items-center w-[500px] rounded-[20px] overflow-hidden text-center">
        <Header
          showAmount
          title={isTimerRunning ? i18n.t("header.previous") : null}
        />
        <LayoutWrapper1xbet>
          <MercadoLogo className="size-[60px] mb-2" />
          <div className="mb-2 text-light-gray text-sm max-w-xs">
            {i18n.t("mercado.checkout.title")}
          </div>

          <div className="flex gap-2 mb-4 font-bold text-black text-3xl tracking-wider">
            {formatCurrency(
              transactionData?.merchant_request_amount,
              transactionData?.merchant_request_currency_iso,
              true,
            )}
          </div>

          <div className="flex border-2 border-mercado-primary border-opacity-30 justify-start items-start relative gap-4 pl-3 pr-5 py-3 rounded-[10px] overflow-hidden w-full text-[0.8rem] text-left">
            <WarningIcon className="h-auto w-[3rem] z-10 mt-2" />
            <span className="leading-normal text-mercado-dark">
              {i18n.t("mercado.checkout.warning")}
            </span>
            <div className="absolute top-0 left-0 size-full bg-mercado-primary opacity-10" />
          </div>

          {isQrPresent ? (
            <QrCode
              value={transactionData?.bank_payment_data?.qr_code}
              alt="QR Code"
            />
          ) : (
            <div className="rounded-lg w-full overflow-hidden my-7 border-2 border-black border-opacity-10">
              <FakeInput1xbet
                label={i18n.t("mercado.checkout.destinataryCBU")}
                value={String(
                  transactionData?.bank_payment_data?.account_id || "",
                )}
                dataCopy={transactionData?.bank_payment_data?.account_id}
                hasAnimation
              />
              <div className="w-full border-t border-black border-opacity-10" />
              <FakeInput1xbet
                label={i18n.t("form.destinataryLabel")}
                value={String(
                  transactionData?.bank_payment_data?.account_holder || "",
                )}
              />
            </div>
          )}

          {dataSource === "merchant" && (
            <>
              <ul className="w-full mb-6 text-sm text-left text-light-gray font-medium">
                {Array.from({ length: QUANTITY_INSTRUCTIONS }).map((_, i) => {
                  const iterationNumber = i + 1;
                  const instructionText = isQrPresent
                    ? i18n.t(`checkout.instructions_qr.${iterationNumber}`)
                    : i18n.t(`checkout.instructions.${iterationNumber}`);
                  return (
                    <li key={iterationNumber} className="flex gap-x-2">
                      <span className="shrink-0">{iterationNumber}.</span>
                      <span>{instructionText}</span>
                    </li>
                  );
                })}
              </ul>

              <p className="w-full text-xs text-center text-light-gray font-medium">
                {i18n.t("mercado.checkout.reminingTime")}
              </p>

              <p className="w-full text-xl mt-3 text-center font-bold text-black flex justify-center items-center">
                {convertMsToTime(counter, "mercado")
                  .split(" : ")
                  .map((part, i) => (
                    <span
                      // eslint-disable-next-line react/no-array-index-key
                      key={i}
                      className="flex items-center after:content-[':'] after:text-light-gray after:font-normal after:mx-1 last:after:content-none"
                    >
                      {part}
                    </span>
                  ))}
              </p>

              {transactionData?.customer?.pending_kyc_url &&
                transactionData?.customer?.level === 1 && (
                  <button
                    type="button"
                    className="flex mb-5 items-center relative gap-5 px-5 py-2 rounded-[10px] overflow-hidden w-full text-[0.8rem] text-left cursor-pointer group"
                    onClick={() =>
                      history.push(
                        `/kyc/2/customer/${transactionData?.customer?.id}/${token}?autoStart=true`,
                      )
                    }
                  >
                    <DocumentIcon className="w-28 z-10" />
                    <span className="leading-snug text-black z-10">
                      <Trans
                        i18nKey="general.identityInstructions"
                        components={{
                          1: (
                            <span className="text-blue-600 underline font-medium" />
                          ),
                        }}
                      />
                    </span>
                    <div className="absolute top-0 left-0 size-full bg-cripten-red opacity-20" />
                  </button>
                )}
              {transactionData?.customer?.pending_kyc_url &&
                transactionData?.customer?.level === 2 && (
                  <button
                    type="button"
                    className="flex mb-5 items-center relative gap-5 px-7 py-2 rounded-[10px] overflow-hidden w-full text-[0.8rem] text-left cursor-pointer group"
                    onClick={() =>
                      history.push(
                        `/kyc/3/customer/${transactionData?.customer?.id}/${token}/sof`,
                        {
                          merchant: transactionData?.merchant,
                          transactionId: id,
                          kycRequired,
                          redirect_urls: {
                            success:
                              transactionData?.redirect_url_success || null,
                            failed:
                              transactionData?.redirect_url_failed || null,
                          },
                        },
                      )
                    }
                  >
                    <SofIcon className="w-32 z-10" />
                    <span className="leading-snug text-black z-10">
                      <Trans
                        i18nKey="general.sofInstructions"
                        components={{
                          1: (
                            <span className="text-blue-600 underline font-medium" />
                          ),
                        }}
                      />
                    </span>
                    <div className="absolute top-0 left-0 size-full bg-cripten-red opacity-20" />
                  </button>
                )}
            </>
          )}
          {validationNeeded && (
            <div className="flex mt-5 bg-cripten-pink items-center relative gap-4 px-5 py-2 rounded-[10px] overflow-hidden w-full text-[0.8rem] text-left">
              <DocumentIcon className="w-28" />
              <span>{i18n.t("success.validationNeeded")}</span>
            </div>
          )}
          {showAlreadyPaid && (
            <span
              role="button"
              tabIndex={0}
              className="text-md mt-4 bg-cripten-blue hover:bg-[#0065d2] text-white hover:text-white focus:text-white cursor-pointer rounded-md px-16 py-2 tracking-widest"
              onClick={() => {
                history.push({
                  pathname: "/transaction-already-paid",
                  state: { transactionData },
                });
              }}
              onKeyDown={(e) =>
                (e.key === "Enter" || e.key === " ") && e.currentTarget.click()
              }
            >
              {i18n.t("mercado.checkout.alreadyTransfer")}
            </span>
          )}

          <Button
            type="button"
            mercado
            handleClick={() => {
              if (isAndroid()) {
                window.location.href =
                  "intent://www.mercadopago.com.ar/#Intent;scheme=https;package=com.mercadopago.wallet;end";
              } else if (isIOS()) {
                window.location.href = "mercadopago://";
                setTimeout(() => {
                  window.open(
                    "https://www.mercadopago.com.ar/",
                    "_blank",
                    "noopener,noreferrer",
                  );
                }, 500);
              } else {
                window.open(
                  "https://www.mercadopago.com.ar/",
                  "_blank",
                  "noopener,noreferrer",
                );
              }
            }}
          >
            {i18n.t("mercado.checkout.payInMp")}
          </Button>
        </LayoutWrapper1xbet>
      </div>
    </div>
  );
}

export default TransactionDetails1xbet;
